import React, { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { HandHeart, ShieldCheck, Copy, CheckCircle, Clock, ArrowRight } from "@phosphor-icons/react";

export default function NominateDonor() {
  const { user } = useAuth();
  const isChecker = user?.role === "super_admin";
  const [form, setForm] = useState({ name: "", email: "", phone: "", pan_no: "", address: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setResult(null);
    setSubmitting(true);
    try {
      const res = await api.post("/admin/donors/nominate", form);
      setResult(res.data);
      if (res.data.status === "created") {
        toast.success("Donor account created");
      } else {
        toast.success("Nomination submitted for super-admin approval");
      }
      setForm({ name: "", email: "", phone: "", pan_no: "", address: "", note: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const copyPw = async () => {
    if (!result?.temp_password) return;
    await navigator.clipboard.writeText(result.temp_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <DashboardLayout
      title="Nominate Donor"
      subtitle={isChecker ? "Create a donor account directly." : "Nominate a donor — super-admin will review and approve."}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-5xl">
        <form onSubmit={onSubmit} className="lg:col-span-2 vs-card p-6 space-y-4" data-testid="nominate-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Donor name *</Label>
              <Input
                data-testid="nom-name"
                required
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className="bg-white"
                placeholder="Full name"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                data-testid="nom-email"
                required
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="bg-white"
                placeholder="donor@example.com"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Phone</Label>
              <Input
                data-testid="nom-phone"
                inputMode="numeric"
                pattern="[0-9+\-\s]*"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value.replace(/[^\d+\-\s]/g, ""))}
                className="bg-white font-mono"
              />
            </div>
            <div>
              <Label>PAN card *</Label>
              <Input
                data-testid="nom-pan"
                required
                maxLength={10}
                value={form.pan_no}
                onChange={(e) => set("pan_no", e.target.value.toUpperCase())}
                className="bg-white font-mono"
                placeholder="AAAPL1234C"
              />
            </div>
            <div>
              <Label>Address *</Label>
              <Input
                data-testid="nom-address"
                required
                value={form.address}
                onChange={(e) => set("address", e.target.value)}
                className="bg-white"
                placeholder="Postal address for 80G receipts"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Why this donor? <span className="text-[10px] text-[var(--vs-text-secondary)] font-normal">(visible to super-admin)</span></Label>
              <Textarea
                data-testid="nom-note"
                value={form.note}
                onChange={(e) => set("note", e.target.value)}
                className="bg-white"
                rows={3}
                placeholder="e.g. Long-time community supporter, interested in monthly sponsorship of 2 students…"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button
              type="submit"
              disabled={submitting}
              data-testid="nom-submit"
              className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)]"
            >
              <HandHeart size={14} className="mr-1.5" />
              {(() => {
                if (submitting) return "Submitting…";
                return isChecker ? "Create donor account" : "Submit nomination";
              })()}
              <ArrowRight size={14} className="ml-1.5" />
            </Button>
            <div className="flex items-center gap-1.5 text-xs font-mono text-[var(--vs-secondary)]">
              <ShieldCheck size={14} weight="fill" />
              {isChecker ? "Created immediately" : "Maker-Checker — super-admin approves"}
            </div>
          </div>
        </form>

        <motion.aside
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          className="vs-card p-6 self-start space-y-3"
        >
          <div className="eyebrow">How it works</div>
          {isChecker ? (
            <ol className="space-y-2 text-sm text-[var(--vs-text-secondary)]">
              <li><span className="text-[var(--vs-text)] font-semibold">1.</span> Fill the form and submit.</li>
              <li><span className="text-[var(--vs-text)] font-semibold">2.</span> Account is created immediately with an auto-generated temporary password.</li>
              <li><span className="text-[var(--vs-text)] font-semibold">3.</span> Share the password with the donor securely; they reset on first login.</li>
            </ol>
          ) : (
            <ol className="space-y-2 text-sm text-[var(--vs-text-secondary)]">
              <li><span className="text-[var(--vs-text)] font-semibold">1.</span> You submit a nomination here.</li>
              <li><span className="text-[var(--vs-text)] font-semibold">2.</span> Super-admin reviews on the Approvals page.</li>
              <li><span className="text-[var(--vs-text)] font-semibold">3.</span> On approve, the donor account is created and the super-admin shares credentials with the donor.</li>
            </ol>
          )}
          <div className="vs-divider" />
          <div className="text-[11px] font-mono text-[var(--vs-text-secondary)]">
            Donor PII is encrypted at rest. Approvals are audit-logged.
          </div>
        </motion.aside>
      </div>

      {/* Result panel */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="nominate-result"
          className="mt-6 vs-card p-6 max-w-3xl"
        >
          {result.status === "created" ? (
            <>
              <div className="flex items-center gap-2 text-[var(--vs-success)] mb-3">
                <CheckCircle size={20} weight="duotone" />
                <span className="font-display font-semibold">Donor account created</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="eyebrow">Email</div>
                  <div className="font-mono mt-1">{result.email}</div>
                </div>
                <div>
                  <div className="eyebrow">Temporary password</div>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="font-mono text-base bg-[var(--vs-bg-subtle)] px-2 py-1 rounded">{result.temp_password}</span>
                    <Button size="sm" variant="outline" onClick={copyPw} data-testid="copy-temp-pw">
                      {copied ? <CheckCircle size={12} className="mr-1.5" /> : <Copy size={12} className="mr-1.5" />}
                      {copied ? "Copied" : "Copy"}
                    </Button>
                  </div>
                </div>
              </div>
              <p className="text-xs text-[var(--vs-text-secondary)] mt-4">{result.note}</p>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-[var(--vs-warning)] mb-3">
                <Clock size={20} weight="duotone" />
                <span className="font-display font-semibold">Pending super-admin review</span>
              </div>
              <p className="text-sm text-[var(--vs-text-secondary)]">{result.summary}</p>
              <div className="mt-3 text-[11px] font-mono text-[var(--vs-text-secondary)]">
                Approval ID: {result.approval_id}
              </div>
            </>
          )}
        </motion.div>
      )}
    </DashboardLayout>
  );
}
